import LoadingButton from "@/components/LoadingButton";
import { StepHeader } from "@/components/onboarding/StepHeader";
import { Button } from "@/components/ui/button";
import { SelectDropdown } from "@/components/ui/dropdown-menu";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Slider } from "@/components/ui/slider";
import { api, handleApiRequest } from "@/lib/utils";

import type { CompleteOnboardingFormData } from "@/lib/zod-schemas/onboarding";
import type { OnboardingStepProps } from "@/types/onboarding";

const ANNUAL_REVENUE_OPTIONS = [
  { label: "$0K - $200K", value: "$0K - $200K" },
  { label: "$200K - $500K", value: "$200K - $500K" },
  { label: "$500K - $1M", value: "$500K - $1M" },
  { label: "$1M - $5M", value: "$1M - $5M" },
  { label: "$5M+", value: "$5M+" },
];

const YEARS_IN_BUSINESS_OPTIONS = [
  { label: "Less than 1 year", value: "Less than 1 year" },
  { label: "1-2 years", value: "1-2 years" },
  { label: "3-5 years", value: "3-5 years" },
  { label: "6-10 years", value: "6-10 years" },
  { label: "10+ years", value: "10+ years" },
];

const MAX_TEAM_MEMBERS = 16;

export function CompanyDetailsStatisticsStep({
  form,
  onNext,
  isOptional,
}: OnboardingStepProps<CompleteOnboardingFormData>) {
  const userProvidedTeamMemberCount = form.getValues("userProvidedTeamMemberCount");

  const handleContinue = async (websiteId: string | undefined, data: CompleteOnboardingFormData) => {
    if (!websiteId) {
      return;
    }
    await handleApiRequest({
      makeRequest: async () => {
        const response = await api.put(`/api/sites/${websiteId}/extra-info`, data);
        return response.data.extraInfo;
      },
      successCallback: () => {
        onNext();
      },
    });
  };

  const handleTeamMembersChange = (amount: number) => {
    form.setValue("userProvidedTeamMemberCount", amount);
    form.trigger("userProvidedTeamMemberCount");
  };

  const handleSliderChange = (values: number[]) => {
    const amount = values[0];
    handleTeamMembersChange(amount);
  };

  return (
    <div className="flex flex-col justify-start lg:justify-center h-full px-5 lg:px-40 py-6 lg:py-0 w-full">
      <StepHeader
        title="Tell us a little about your business."
        subtitle="These details help us customize your experience. All fields are optional and can be updated later."
      />

      <div className="mt-6 lg:mt-12 w-full flex flex-col lg:gap-9 gap-6">
        <div className="flex gap-6 w-full lg:flex-row flex-col">
          <FormField
            control={form.control}
            name="annualRevenue"
            render={({ field }) => (
              <FormItem className="flex flex-col mb-0 w-full">
                <FormLabel className="text-ash-700 font-semibold lg:text-base text-sm">Annual Revenue</FormLabel>
                <FormControl>
                  <SelectDropdown
                    items={ANNUAL_REVENUE_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    triggerClassName="text-sm mb-0 h-fit lg:px-4 lg:py-3.5 px-3 py-2 rounded-xl border-ash-300 bg-white"
                    placeholder="Revenue..."
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="yearsInBusiness"
            render={({ field }) => (
              <FormItem className="flex flex-col mb-0 w-full">
                <FormLabel className="text-ash-700 font-semibold lg:text-base text-sm">Years in Operation</FormLabel>
                <FormControl>
                  <SelectDropdown
                    items={YEARS_IN_BUSINESS_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    triggerClassName="text-sm mb-0 h-fit lg:px-4 lg:py-3.5 px-3 py-2 rounded-xl border-ash-300 bg-white"
                    placeholder="Years..."
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="w-full max-w-none flex flex-col gap-6">
          <div className="flex items-start justify-between">
            <div className="text-ash-700 font-semibold lg:text-base text-sm tracking-[-1%]">Team Members</div>
            <div className="text-3xl font-clash font-medium text-ash-900 tracking-[-1%]">
              {userProvidedTeamMemberCount}
              {userProvidedTeamMemberCount === MAX_TEAM_MEMBERS ? `+` : ``}
            </div>
          </div>

          <Slider
            value={[userProvidedTeamMemberCount]}
            defaultValue={[1]}
            onValueChange={handleSliderChange}
            min={1}
            max={MAX_TEAM_MEMBERS}
            step={1}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex gap-4 lg:mt-12 mt-auto">
        <LoadingButton
          type="button"
          variant="rebolt-secondary"
          size="md"
          className="justify-center lg:max-w-fit tracking-tight lg:py-3 py-2"
          onClick={async () => {
            await handleContinue(form.getValues("reboltWebsiteId"), form.getValues());
          }}
        >
          Continue
        </LoadingButton>
        {isOptional && (
          <Button type="button" variant="rebolt-link" size="link" className="text-sm lg:text-base" onClick={onNext}>
            Skip for now
          </Button>
        )}
      </div>
    </div>
  );
}
